"use client";

import { useEffect } from "react";
import { useRouter, usePathname } from "next/navigation";
import { useAuth } from "@/contexts/auth-context";
import { Loader2 } from "lucide-react";
import type { UserRole } from "@/types/auth";
import type { PermissionModuleCode } from "@/types/permissions";
import { hasRequiredRole } from "@/lib/authorization";

interface ProtectedRouteProps {
  children: React.ReactNode;
  requiredRole?: UserRole;
  requiredRoles?: UserRole[];
  requiredModule?: PermissionModuleCode;
  /** Qualquer um dos módulos libera acesso — usado quando uma rota é compartilhada por páginas com módulos diferentes (ex.: layout de /estoque/contagem cobrindo tanto Contagem quanto Auditoria). */
  requiredModules?: PermissionModuleCode[];
}

export function ProtectedRoute({ children, requiredRole, requiredRoles, requiredModule, requiredModules }: ProtectedRouteProps) {
  const { user, isLoading, hasModuleAccess } = useAuth();
  const router = useRouter();
  const pathname = usePathname();

  const lacksRoleAccess = Boolean(
    user &&
    (requiredRole || requiredRoles?.length) &&
    !hasRequiredRole(user.roles, requiredRole, requiredRoles)
  );
  const lacksModuleAccess = Boolean(
    (requiredModule && !hasModuleAccess(requiredModule)) ||
    (requiredModules?.length && !requiredModules.some((code) => hasModuleAccess(code)))
  );

  useEffect(() => {
    if (!isLoading && !user) {
      router.push(`/login?redirect=${encodeURIComponent(pathname)}`);
    }
  }, [user, isLoading, router, pathname]);

  useEffect(() => {
    if (!isLoading && user) {
      if (lacksRoleAccess || lacksModuleAccess) {
        router.push('/');
      }
    }
  }, [user, isLoading, lacksModuleAccess, lacksRoleAccess, router]);

  if (isLoading) {
    return (
      <div className="min-h-dvh flex items-center justify-center bg-background">
        <Loader2 className="size-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!user) {
    return null;
  }

  if (lacksRoleAccess || lacksModuleAccess) {
    return (
      <div className="min-h-dvh flex items-center justify-center bg-background">
        <p className="text-[length:var(--text-sm)] text-muted-foreground">
          Você não tem permissão para acessar esta página
        </p>
      </div>
    );
  }

  return <>{children}</>;
}
